"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CarouselControls from "../carousel/carousel-controls";
import GalleryCard from "../card/gallery-card";
import OverlayCard from "../card/overlay-card";
import { Item, GalleryProps } from "@/types/item";
import { useResponsiveCarousel } from "@/hooks/useResponsiveCarousel";
import SectionTitle from "../SectionTitle";

const CARD_WIDTH = 366;
const GAP = 16;
const MAX_PER_PAGE = 3;

export default function TextGallery(props: GalleryProps) {
	const {
		title1,
		title2,
		services,
		leftIcon,
		rightIcon,
		hideIconOnMobile,
		isAdmin = false,
		isNews = false,
		noTopPadding = false,
		bgClass,
	} = props;

	const [selectedService, setSelectedService] = useState<Item | null>(null);
	const [items, setItems] = useState<Item[]>(services);

	const {
		wrapperRef,
		page,
		setPage,
		perPage,
		pageCount,
		cardWidth,
		trackWidth,
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
	} = useResponsiveCarousel({
		cardWidth: CARD_WIDTH,
		gap: GAP,
		maxPerPage: MAX_PER_PAGE,
		itemCount: items.length,
	});

	const startIndex = page * perPage;
	const visibleItems = items.slice(startIndex, startIndex + perPage);

	return (
		<section
			className={`py-24 px-8 md:px-12 ${noTopPadding ? "pt-0" : ""} ${
				bgClass ?? ""
			}`}
		>
			<SectionTitle
				title1={title1}
				title2={title2}
				leftIcon={leftIcon}
				rightIcon={rightIcon}
				hideIconOnMobile={hideIconOnMobile}
			/>

			<div ref={wrapperRef} className="relative mx-auto pb-12 w-full">
				<div
					className="overflow-hidden touch-pan-x"
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
				>
					<AnimatePresence mode="wait">
						<motion.div
							key={`${page}-${perPage}`}
							className="flex gap-4 justify-center mx-auto"
							style={{ maxWidth: trackWidth }}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							transition={{ duration: 0.25, ease: "easeOut" }}
						>
							{visibleItems.map((service) => (
								<div
									key={service.id}
									className="flex-shrink-0"
									style={{ width: cardWidth }}
								>
									<GalleryCard
										id={service.id}
										title={service.title}
										description={service.description}
										onClick={() => setSelectedService(service)}
									/>
								</div>
							))}
						</motion.div>
					</AnimatePresence>
				</div>

				{pageCount > 1 && (
					<div className="absolute bottom-0 left-1/2 -translate-x-1/2 mt-4">
						<CarouselControls
							total={pageCount}
							activeIndex={page}
							onPrev={() => setPage((prev) => Math.max(0, prev - 1))}
							onNext={() =>
								setPage((prev) => Math.min(pageCount - 1, prev + 1))
							}
							disablePrev={page === 0}
							disableNext={page === pageCount - 1}
							onDotClick={(index) => setPage(index)}
						/>
					</div>
				)}
			</div>

			<OverlayCard
				selectedItem={selectedService}
				onClose={() => setSelectedService(null)}
				isAdmin={isAdmin}
				isNews={isNews}
				onDelete={(id) =>
					setItems((prev) => prev.filter((item) => item.id !== id))
				}
			/>
		</section>
	);
}
